// src/utils/packersPlannerViewUtils.js
// Normalisation and safe matching for Packers Toolkit "Views" (settings/production.plannerViews)
// Membership uses the same orderMatchesPlannerView contract as Planner / Sticky packing walk.

import {
    isPlannerViewActive,
    orderMatchesPlannerView,
    formatPlannerViewWeekdaysLabel,
} from '../pages/ProductionToolkit/components/ProductionPlanning/plannerViewSlice';
import { normalizeOrderDate } from './orderDateUtils';

/**
 * @param {unknown} raw
 * @returns {{ views: object[], invalidDropped: number, duplicateIdsDropped: number }}
 */
export function normalizePlannerViewsForPackers(raw) {
    if (!Array.isArray(raw)) {
        return { views: [], invalidDropped: 0, duplicateIdsDropped: 0 };
    }

    const seen = new Set();
    const views = [];
    let invalidDropped = 0;
    let duplicateIdsDropped = 0;

    for (const item of raw) {
        if (!item || typeof item !== 'object') {
            invalidDropped += 1;
            continue;
        }
        const id = item.id != null ? String(item.id).trim() : '';
        if (!id) {
            invalidDropped += 1;
            continue;
        }
        if (seen.has(id)) {
            duplicateIdsDropped += 1;
            continue;
        }
        seen.add(id);

        const name = item.name != null ? String(item.name) : '';
        const shippingMethod =
            item.shippingMethod != null ? String(item.shippingMethod) : '';

        views.push({
            ...item,
            id,
            name,
            shippingMethod,
        });
    }

    views.sort((a, b) => {
        const ao = Number(a?.sortOrder);
        const bo = Number(b?.sortOrder);
        const aOk = Number.isFinite(ao);
        const bOk = Number.isFinite(bo);
        if (aOk && bOk && ao !== bo) return ao - bo;
        if (aOk && !bOk) return -1;
        if (!aOk && bOk) return 1;
        return String(a.name || '').localeCompare(String(b.name || ''));
    });

    return { views, invalidDropped, duplicateIdsDropped };
}

/**
 * Safe string for HTML `title` attributes (Firestore text; strip angle brackets / quotes).
 * @param {unknown} s
 * @param {number} [max]
 */
export function safePlannerViewTitleAttr(s, max = 220) {
    const t = String(s ?? '')
        .replace(/[<>"]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max);
    return t || '—';
}

/**
 * @param {object|null|undefined} view
 * @returns {boolean}
 */
export function canApplyPackersPlannerViewFilter(view) {
    return view != null && typeof view === 'object' && isPlannerViewActive(view);
}

/**
 * Normalize Packers order dispatch to YYYY-MM-DD for shared planner view match.
 * @param {object} order
 * @returns {string|null}
 */
export function resolvePackersOrderDispatchIso(order) {
    const raw = order?.dispatchDate ?? order?.['Dispatch Date'] ?? order?.dispatch_date ?? '';
    return normalizeOrderDate(String(raw || '').trim());
}

/**
 * Whether an order row should pass the planner view filter.
 * Same weekday + shipping rules as Production Planner Views / Sticky packing walk.
 * @param {object} order
 * @param {object} view — normalised view object
 * @returns {boolean}
 */
export function orderPassesPackersPlannerView(order, view) {
    if (!canApplyPackersPlannerViewFilter(view)) {
        return true;
    }

    try {
        const dispatchDate = resolvePackersOrderDispatchIso(order);
        if (!dispatchDate) return false;
        return orderMatchesPlannerView(
            {
                ...order,
                dispatchDate,
                shippingMethod: order?.shippingMethod || order?.['Shipping Method'] || '',
            },
            view,
        );
    } catch {
        return false;
    }
}

/**
 * Multi-select Views: order passes if it matches **any** selectable view (OR).
 * @param {object} order
 * @param {object[]} views
 */
export function orderPassesAnyPackersPlannerView(order, views) {
    const list = (Array.isArray(views) ? views : []).filter(canApplyPackersPlannerViewFilter);
    if (list.length === 0) return true;
    return list.some((view) => orderPassesPackersPlannerView(order, view));
}

/**
 * Tooltip line for a view chip (weekdays + shipping).
 * @param {object} view
 */
export function buildPackersPlannerViewChipTitle(view) {
    if (!view || typeof view !== 'object') {
        return '—';
    }
    const days = safePlannerViewTitleAttr(formatPlannerViewWeekdaysLabel(view), 120);
    const ship = safePlannerViewTitleAttr(view.shippingMethod || '', 100);
    return safePlannerViewTitleAttr(`${days} · shipping: ${ship}`, 240);
}

export { formatPlannerViewWeekdaysLabel };
